<template>
  <div
    class="d-flex flex-column align-items-center text-normal-1 text-center py-5 paywall-container position-relative"
  >
    <div class="icon-wrapper flex-center">
      <div class="icon-container flex-center">
        <i class="star-icon"></i>
      </div>
    </div>
    <div class="header text-extra-large bold">
      <slot v-if="$slots.heading" name="heading"></slot>
      <template v-else>
        {{ heading }}
      </template>
    </div>
    <div class="content thin mt-4">
      <slot name="content"></slot>
    </div>
    <user-type-based-renderer :org-id="orgId">
      <div class="d-flex flex-column align-items-center mt-4 pt-2" slot="owner">
        <div class="mb-3 bold" v-if="featureType === FEATURE_TYPE.BOOLEAN">
          Your current plan does not include this feature.
        </div>
        <div class="upgrade-button-wrapper">
          <b-button
            class="upgrade-button py-1 text-large bold px-5"
            @click="onSubmit"
            variant="success"
            pill
            >See Plans to upgrade
          </b-button>
        </div>
      </div>
      <div slot="regular">
        <div class="mt-4">
          <div class="bold" v-if="featureType === FEATURE_TYPE.BOOLEAN">
            Your current plan does not include this feature.
          </div>
          <div>Contact any of the admins below to upgrade</div>
        </div>
        <members-list
          class="mt-4 d-flex justify-content-center"
          :members-list="membersList"
          :org-id="orgId"
          :sub-org-id="subOrgId"
          :limit="5"
          :avatar-size="35"
        />
        <div class="mt-3">
          <b-link
            href="https://thenorth.io/pricing/"
            target="_blank"
            rel="nofollow noopener noreferrer"
          >
            See our plans and pricing
          </b-link>
        </div>
        <div class="mt-3" v-if="learnMoreLink">
          <b-link
            class="text-prime"
            :href="learnMoreLink"
            target="_blank"
            rel="nofollow noopener noreferrer"
          >
            Learn more
          </b-link>
        </div>
      </div>
    </user-type-based-renderer>
  </div>
</template>

<script>
import MembersList from '@/components/MembersList.vue'
import UserTypeBasedRenderer from '@/components/UserTypeBasedRenderer.vue'
import { ADMIN_PANEL_TAB_ID } from '@/constants/adminPanel'
import { FEATURE_TYPE } from '@/constants/featureFlags'
import MembershipsMixin from '@/mixins/MembershipsMixin'
import handleClickNavigation from '@/utils/handleClickNavigation'
import { getAdminPageRouteConfig } from '@/utils/navigationConfigs'
import { BButton, BLink } from 'bootstrap-vue'
import * as R from 'ramda'

/**
 * This component is the wrapper for Paywalls. It provides heading and content structure.
 */
export default {
  name: 'Paywall',
  props: {
    heading: String,
    learnMoreLink: String,
    featureType: {
      default: FEATURE_TYPE.INTEGER,
      type: String
    }
  },
  components: {
    BButton,
    UserTypeBasedRenderer,
    MembersList,
    BLink
  },
  mixins: [MembershipsMixin],
  data() {
    return {
      FEATURE_TYPE
    }
  },
  computed: {
    subOrgId() {
      return this.$store.state.orgSelect.selectedOrgLikeTeamId
    },
    orgId() {
      return this.$store.getters.teamById(this.subOrgId).organizationId
    },
    org() {
      return this.$store.getters.orgById(this.orgId)
    },
    membersList() {
      const sortByFullName = R.sortBy(
        R.compose(
          R.toLower,
          R.path(['user', 'displayName'])
        )
      )
      const members = this.org.memberships?.filter(user => user.type === 'owner') || []
      return sortByFullName(members)
    }
  },
  methods: {
    onSubmit(event) {
      const plansLocation = getAdminPageRouteConfig({
        orgId: this.orgId,
        subOrgId: this.subOrgId,
        query: {
          section: ADMIN_PANEL_TAB_ID.PLANS_TAB_ID
        }
      })
      handleClickNavigation.call(this, event, plansLocation)
    }
  },
  mounted() {
    if (R.isEmpty(this.membersList)) {
      this.updateMemberships(this.orgId)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';

.icon-wrapper {
  @include dimension(100px, 100px);
  border-radius: 50%;
  position: absolute;
  background: $documentColor;
  top: -50px;
  left: 50%;
  transform: translateX(-50%);
}

.icon-container {
  @include dimension(70px, 70px);
  background-color: $activeQuaternary;
  border-radius: 50%;
}

.star-icon {
  @include dimension(30px, 30px);
}

.upgrade-button-wrapper {
  background-color: $activeQuaternary;
  padding: 8px;
  border-radius: 50px;
}

.upgrade-button {
  background-color: $activeSecondary;
  border-color: $activeSecondary;
}

.paywall-container {
  max-width: 450px;
  margin: 0 auto;
}
</style>
